<template>
  <!-- 直播人数热度组件 -->
  <div class="hotv">
    <!-- 显示当前数字 -->
    {{ displayNum || "--" }}
    <!-- 当有增加数值时显示，并且应用动画 -->
    <div class="ani" :class="{ showIncrease: increase }">+{{ increase }}</div>
  </div>
</template>
<script>
import currency from "currency.js";
import dayjs from "dayjs";

const cacheName = "zbrd-history";
const localCache = JSON.parse(localStorage.getItem(cacheName) ?? "{}");

export default {
  name: "CountByStep",
  props: {
    id: [String, Number], // 直播id
    startTime: String, // 开始时间
    endTime: String, // 结束时间
    targetNum: Number, // 目标数量
    animate: {
      type: Boolean,
      default: true,
    }, // 是否使用动画 默认：true
    updateSpeed: {
      type: Array,
      default() {
        return [5, 10];
      },
    }, // 人数变动随机频率[最快s,最慢s]  默认：[5,10]
    targetPercent: {
      type: Array,
      default() {
        return [25, 40, 50, 55, 65, 70, 85, 90, 95, 100];
      },
    }, // 每10%时间间隔总数量目标百分比 默认：[25, 40, 50, 55, 65, 70, 85, 90, 95, 100]
  },
  data() {
    return {
      curPercent: undefined,
      curNumber: undefined,
      increase: undefined,
    };
  },
  watch: {
    id() {
      this.init();
    },
    targetNum() {
      this.init();
    },
  },
  created() {
    this.init();
  },
  methods: {
    init() {
      delete localCache[this.id];
      localStorage.setItem(cacheName, JSON.stringify(localCache));
      this.curPercent = undefined;
      this.curNumber = undefined;
      this.increase = undefined;
      this.execBundle();
      this.execRandomDelay(this.execBundle);
    },
    // 获取各时间节点
    getTimes() {
      const start = dayjs(this.startTime).unix();
      const end = dayjs(this.endTime).unix();
      const now = dayjs().unix();
      const duration = end - start;
      return { start, end, now, duration };
    },
    // 计算当前百分比
    getCurPercent() {
      const { now, start, duration } = this.getTimes();
      // console.log({ now, start, duration })
      this.curPercent = Math.min(((now - start) / duration) * 100, 100);
    },
    // 计算应该展示的数字
    async getCurNum() {
      // 本地缓存数字
      const localNum = localCache?.[this.id] ?? 0;
      const { max, offset, numPerSec, num } = this.percentCount;

      console.log({ max, offset, numPerSec, num, localNum });

      if (num > 0 && localNum < num) {
        localCache[this.id] = num;
        localStorage.setItem(cacheName, JSON.stringify(localCache));
        this.curNumber = currency(num, { precision: 0, symbol: "" }).value;
        return;
      }

      if (localNum >= this.targetNum || num >= this.targetNum) {
        console.log("已经达到目标值", {
          num,
          localNum,
          targetNum: this.targetNum,
        });
        localCache[this.id] = this.targetNum;
        localStorage.setItem(cacheName, JSON.stringify(localCache));
        this.curNumber = this.targetNum;
        return;
      }

      let incNum = 0;
      if (max <= localNum && this.targetNum > localNum) {
        // 本地数字过大启用假冒加数字
        if (localNum < 999) {
          // 小于千位数, 个位数随机增加
          incNum = Math.floor(Math.random() * 5);
        }
        if (localNum >= 999) {
          // 千位数, 2位数随机增加
          incNum = Math.floor(Math.random() * 15);
        }
        console.log("fakeInc", { incNum, numPerSec, offset });
      } else {
        incNum = numPerSec * (this.randomMs() / 1000);
        // 随机浮动
        incNum += Math.floor((Math.random() * incNum) / 2);
        console.log("inc", { incNum, numPerSec, offset });
      }
      const incInt = currency(incNum, { precision: 0, symbol: "" }).value;
      if (this.animate) {
        // 有动画效果
        this.increase = incInt;
        setTimeout(() => {
          this.increase = undefined;
        }, 1000);
      }
      await this.$nextTick();

      localCache[this.id] = localNum + incInt;
      localStorage.setItem(cacheName, JSON.stringify(localCache));
      this.curNumber = localNum + incInt;
    },
    // 时间间隔
    randomMs() {
      const [min, max] = this.updateSpeed;
      return (
        Math.floor(Math.random() * (max * 1000 - min * 1000 + 1)) + min * 1000
      );
    },
    // 设置随机间隔查询
    execRandomDelay(exec) {
      const delay = this.randomMs();
      setTimeout(() => {
        exec();
        // console.log('间隔:', delay)
        this.execRandomDelay(exec);
      }, delay);
    },
    // 需要轮询操作的操作集合
    async execBundle() {
      this.getCurPercent();
      await this.$nextTick();
      await this.getCurNum();
    },
  },
  computed: {
    // 下一个目标的百分比跟数量
    nextRange() {
      if (!this.curPercent) return undefined;
      const range = this.curPercent / 10;
      const rangeEnd = Math.ceil(range);
      const endPercent = this.targetPercent[rangeEnd - 1];
      const endNum = (this.targetNum / 100) * endPercent;
      return [endPercent, endNum];
    },
    // 到达下一个目标数量还剩余时长
    nextDuration() {
      if (!this.nextRange) {
        return undefined;
      }
      const { duration } = this.getTimes();
      const durationPercent = this.nextRange[0] - this.curPercent;
      return (duration / 100) * durationPercent;
    },
    // 优化展示数字
    displayNum() {
      if (!this.curNumber) return;
      const number = Math.round(this.curNumber);
      const w = currency(number, {
        precision: 1,
        symbol: "",
      }).divide(10000);
      const q = currency(number, {
        precision: 1,
        symbol: "",
      }).divide(1000);
      if (number > 9999) {
        return `${w.format()}万+` + '人看过';
      }
      if (number > 999) {
        return `${q.format()}千+` + '人看过';
      }
      return number + '人看过';
    },
    // 根据百分比得到应该直接显示的数字
    percentCount() {
      if (!this.curPercent) {
        // 没计算好的时候可以展示loading
        return undefined;
      }
      // 当前阶段可以最大的数字
      const max = this.nextRange[1];
      // 原始百分比当前位置数量
      const num = (this.targetNum / 100) * this.curPercent;
      // 原始当前数量距离下一个目标数据的差值
      const offset = max - num;
      // 差值均摊到每一秒
    //   console.log("nextDuration", this.nextDuration);
      //   const numPerSec = (offset / this.nextDuration) * 1000;
      const numPerSec = offset / this.nextDuration;
      return {
        max,
        num,
        offset,
        numPerSec,
      };
    },
  },
};
</script>
<style lang="less">
:scope {
  --rd-color: chocolate;
}
.hotv {
  position: relative;
  display: inline-block;
}
.hotv .ani {
  --rd-color: chocolate;
  display: inline-block;
  text-align: center;
  position: absolute;
  right: 0;
  left: 0;
  bottom: 0;
  opacity: 0;
  color: var(--rd-color);
  font-size: 0.8em;
}
.hotv .ani.showIncrease {
  animation: increment 1s;
}
@keyframes increment {
  0% {
    opacity: 0;
    transform: translateY(0);
  }
  50% {
    opacity: 1;
    transform: translateY(-80%);
  }
  100% {
    opacity: 0;
    transform: translateY(-120%);
  }
}
</style>
